// Logo
// Features:
// - Renders the cabin sign/logo using a custom shader
// - Animates visibility (opacity + snow reveal) based on state
// - Mixes sign texture with snowflake mask

import { useEffect, useMemo } from 'react'
import { useTexture } from '@react-three/drei'
import * as THREE from 'three'
import gsap from 'gsap'

import { useActions, LOGO_STATE } from '../store/useActions'

import logoVertex from './shaders/logo/vertex.glsl'
import logoFragment from './shaders/logo/fragment.glsl'

import logoTextureURL from '/textures/cabin_sign.png'
import snowflakeTextureURL from '/textures/alpha_snow_bw.png'

const LOGO_CONFIG = {
    size: 11,
    position: [-6.1, 7.1, 10.0],
    rotation: [0, 1.4, 0],
    color: '#ffffff',
    signScale: 0.85,
}

export default function Logo() {
    const logoState = useActions((state) => state.logoState)

    // Textures
    const logoTexture = useTexture(logoTextureURL, (texture) => {
        texture.flipY = true
    })

    const snowflakeTexture = useTexture(snowflakeTextureURL, (texture) => {
        texture.flipY = true
    })

    // Geometry
    const geometry = useMemo(
        () => new THREE.PlaneGeometry(LOGO_CONFIG.size, LOGO_CONFIG.size),
        []
    )

    // Material
    const material = useMemo(() => {
        return new THREE.ShaderMaterial({
            uniforms: {
                uSign: { value: logoTexture },
                uSnow: { value: snowflakeTexture },
                uColor: { value: new THREE.Color(LOGO_CONFIG.color) },
                uOpacity: { value: 1.0 }, // Starts visible (default) or handled by effect
                uSignScale: { value: LOGO_CONFIG.signScale },
                uSnowScale: { value: 0.035 }, // Initial snow mask state
            },
            vertexShader: logoVertex,
            fragmentShader: logoFragment,
            transparent: true,
        })
    }, [logoTexture, snowflakeTexture])

    // Animation: State transition
    useEffect(() => {
        const isOpen = logoState === LOGO_STATE.opened

        // Animate Snow Reveal
        gsap.to(material.uniforms.uSnowScale, {
            value: isOpen ? 1.0 : 0.035,
            duration: 2.0,
            ease: 'power2.inOut',
            overwrite: 'auto',
        })

        // Animate Opacity
        gsap.to(material.uniforms.uOpacity, {
            value: isOpen ? 1.0 : 0.0,
            duration: 2.0,
            ease: 'power2.inOut',
            overwrite: 'auto',
        })
    }, [material, logoState])

    return (
        <mesh
            geometry={geometry}
            material={material}
            position={LOGO_CONFIG.position}
            rotation={LOGO_CONFIG.rotation}
        />
    )
}
